import { ProductAttribute, ProductAttributeKeys } from "@wttb/product-configurator";
import cn from "clsx";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";

import { Input } from "@/common/components/form/input";
import { debounce } from "@/common/utils/debounce";
import { useStoreProduct } from "@/features/product/contexts/product-context";
import { BindingSettings } from "./binding-settings";
import { SpineResult } from "./spine-result";
import { BindingType, calculateSpineWidth, validatePages } from "./utils";

const defaultBindingSettings = {
  spine: false,
  pages: { min: 0, max: 0 },
  caseAllowance: 0,
  multiplesValue: 0,
};

const BINDING_ATTR_KEY = "binding";

type PagesAttributeProps = {
  attribute: ProductAttribute;
  className?: string;
};

export function PagesAttribute({ attribute, className }: PagesAttributeProps) {
  const t = useTranslations("custom-attributes-forms");
  const { config, setCustomConfigValue, attributes, attributesHash, thicknessesMap } =
    useStoreProduct();

  const materialThickness = useMemo(() => {
    if (!thicknessesMap) return;
    // Find selected inner material
    const material =
      attributesHash[ProductAttributeKeys.InnerMaterial]?.[
        config?.[ProductAttributeKeys.InnerMaterial] as string
      ];
    // If no stock settings return
    if (!material || !material.settings.stock) return;
    // Find selected material weight if split configuration
    const materialWeight =
      attributesHash[ProductAttributeKeys.InnerMaterialWeight]?.[
        config?.[ProductAttributeKeys.InnerMaterialWeight] as string
      ];
    // If split configuration return thickness based on innerMaterial stock and materialWeight weight
    if (materialWeight && materialWeight.settings.weight) {
      const key = `${material.settings.stock as string}-${materialWeight.settings.weight as string}`;
      return thicknessesMap[key];
    }
    if (!material.settings.weight) return;
    const key = `${material.settings.stock as string}-${material.settings.weight as string}`;

    return thicknessesMap[key];
  }, [config, thicknessesMap, attributesHash]);

  const bindingAttribute = useMemo(
    () => attributes.find((attr) => attr.key === BINDING_ATTR_KEY),
    [attributes],
  );
  const bindingValue = useMemo(
    () =>
      config &&
      bindingAttribute &&
      bindingAttribute?.values.find((val) => val.id === config[bindingAttribute?.key]),
    [bindingAttribute, config],
  );
  const settings = useMemo<BindingType>(() => {
    if (!bindingValue || !bindingAttribute) return defaultBindingSettings;
    const settings = bindingValue.settings as unknown as BindingType;
    return {
      ...settings,
      pages: {
        min: settings?.pages?.min,
        max: settings?.pages?.max,
      },
    };
  }, [bindingAttribute, bindingValue]);
  const {
    register,
    getValues,
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<{ pages: number; spine: number }>({
    mode: "onChange",
    defaultValues: {
      pages: config?.[attribute.key] as number,
      spine: config?.[ProductAttributeKeys.Spine] as number,
    },
  });
  const spine = watch("spine");

  // If binding attribute will be changed or thickness (material & materialWeight)
  // We should recalculate spine width and reset pages
  useEffect(() => {
    handleChange();
    trigger("pages");
  }, [bindingValue, materialThickness]);

  const changePagesCount = (pages: number | null, spine: number) => {
    setCustomConfigValue(attribute.key, pages);
    setCustomConfigValue("spine", spine);
  };

  const setPagesCount = useCallback(debounce(changePagesCount, 1000), []);

  if (!bindingAttribute || !bindingValue) return null;

  const handleChange = () => {
    const pages = getValues("pages");
    const isInValid = validatePages(pages, settings);
    let spine = 0;

    if (isInValid) {
      setValue("spine", spine);
      return setPagesCount(null, spine);
    }

    if (settings.spine) {
      spine = calculateSpineWidth(pages, settings.caseAllowance, materialThickness);
      setValue("spine", spine);
    }

    return setPagesCount(pages, spine);
  };

  return (
    <div className={cn("flex w-full items-start justify-between", className)}>
      <form noValidate className="flex w-[46%] flex-col">
        <div className="md mb-5 flex flex-col md:flex-row md:items-center md:justify-start lg:flex-col lg:items-start 2xl:flex-row 2xl:items-center">
          <Input
            variant="solid"
            type="number"
            {...register("pages", {
              valueAsNumber: true,
              required: t("error-required"),
              onChange: handleChange,
              validate: {
                isPositive: (v) => Number(v) >= 0 || t("error-positive"),
                limit: (v) =>
                  (Number(v) >= settings.pages.min && Number(v) <= settings.pages.max) ||
                  (t("error-pages-limit", {
                    min: settings.pages.min,
                    max: settings.pages.max,
                  }) as string),
                multiples: (v) =>
                  Number(v) % settings.multiplesValue === 0 ||
                  t("error-pages-multiples", { multiples: settings.multiplesValue }),
              },
            })}
            className="mb-2 mr-3 w-[88px] md:mb-0 md:w-[120px] lg:mb-2 lg:w-[88px] 2xl:mb-0 2xl:w-[120px]"
            inputClassName={cn("h-12", { "!border-danger !focus:border-danger": errors.pages })}
          />
          {errors.pages && <span className="text-danger text-xs">{errors.pages?.message}</span>}
        </div>
        <BindingSettings
          min={settings.pages.min}
          max={settings.pages.max}
          multiples={settings.multiplesValue}
        />
      </form>
      {settings.spine && <SpineResult spine={spine} className="w-[46%]" />}
    </div>
  );
}
